import { useState } from "react";
import { ScrollReveal } from "@/components/base/ScrollAnimations";
import { newsItems } from "@/mocks/newsData";
import { Link } from "react-router-dom";

const categoryColors: Record<string, string> = {
  publication: "bg-tie-teal/10 text-tie-teal",
  conference: "bg-tie-navy/10 text-tie-navy",
  announcement: "bg-amber-50 text-amber-700",
  milestone: "bg-emerald-50 text-emerald-700",
};

const categoryLabels: Record<string, string> = {
  publication: "Publication",
  conference: "Conference",
  announcement: "Announcement",
  milestone: "Milestone",
};

const tagFilterLabel = "TIE Grand Launch";

function getYouTubeThumbnail(url: string) {
  let id = "";
  const match = url.match(/[?&]v=([^&]+)/);
  if (match) {
    id = match[1];
  } else {
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) id = shortMatch[1];
  }
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
}

function isVideoUrl(url: string) {
  return url.includes("youtube.com") || url.includes("youtu.be");
}

export default function PressGrid() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(9);

  // const pressItems = newsItems.filter((n) => n.source === "Textile Today");
  const pressItems = newsItems;
  const grandLaunchCount = pressItems.filter((n) => n.tag === tagFilterLabel).length;

  const categories = ["all", "announcement", "conference", "milestone", "publication"];

  const filtered = activeTag === tagFilterLabel
    ? pressItems.filter((n) => n.tag === tagFilterLabel)
    : activeCategory === "all"
    ? pressItems
    : pressItems.filter((n) => n.category === activeCategory);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  function handleCategoryClick(cat: string) {
    setActiveTag(null);
    setActiveCategory(cat);
    setVisibleCount(9);
  }

  function handleTagClick() {
    setActiveTag(tagFilterLabel);
    setActiveCategory("all");
    setVisibleCount(9);
  }

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <ScrollReveal>
            <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-tie-teal/10 mb-4 md:mb-6">
                <span className="text-xs font-semibold text-tie-teal uppercase tracking-wider">
                  / All Press Releases
                </span>
              </div>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-tie-text-primary leading-tight">
                Complete Archive
              </h2>
              <p className="mt-3 md:mt-4 text-base text-tie-text-secondary">
                All official press releases and partnership announcements from Textile Innovation Exchange.
              </p>
            </div>
          </ScrollReveal>

          {/* Category Filter */}
          <ScrollReveal delay={100}>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-10 md:mb-14">
            {/* Special TIE Grand Launch tab */}
              <button
                onClick={handleTagClick}
                className={`inline-flex items-center gap-1.5 px-4 md:px-5 py-2.5 rounded-full text-xs md:text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer border ${
                  activeTag === tagFilterLabel
                    ? "bg-emerald-700 text-white border-emerald-700"
                    : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200"
                }`}
              >
                <i className="ri-star-line" />
                TIE Grand Launch
                <span className={`ml-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${
                  activeTag === tagFilterLabel ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-700"
                }`}>
                  {grandLaunchCount}
                </span>
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCategoryClick(cat)}
                  className={`inline-flex items-center gap-1.5 px-4 md:px-5 py-2.5 rounded-full text-xs md:text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer border ${
                    activeTag === null && activeCategory === cat
                      ? "bg-tie-navy text-white border-tie-navy"
                      : "bg-white text-tie-text-secondary hover:bg-tie-grey border-tie-grey-dark"
                  }`}
                >
                  {cat === "all" ? "All Releases" : categoryLabels[cat]}
                  <span className={`ml-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${
                    activeTag === null && activeCategory === cat ? "bg-white/20 text-white" : "bg-tie-grey text-tie-text-muted"
                  }`}>
                    {cat === "all" ? pressItems.length : pressItems.filter((n) => n.category === cat).length}
                  </span>
                </button>
              ))}
            </div>
          </ScrollReveal>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {visible.map((item, index) => (
              <ScrollReveal key={item.id} delay={index * 80}>
                <article className="group h-full bg-tie-grey-light rounded-2xl overflow-hidden border border-tie-grey-dark hover:border-tie-teal/30 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <Link
                    to={`/press-release/${item.id}`}
                    className="block relative h-44 md:h-52 overflow-hidden"
                  >
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : isVideoUrl(item.externalUrl) ? (
                      <>
                        <img
                          src={getYouTubeThumbnail(item.externalUrl)}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-14 h-14 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
                            <i className="ri-play-fill text-2xl text-white" />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-tie-navy/80 to-tie-navy flex items-center justify-center">
                        <div className="text-center px-4">
                          <i className="ri-newspaper-line text-3xl text-white/40 mb-2" />
                          <p className="text-xs text-white/60">{item.source}</p>
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-tie-navy/40 to-transparent" />
                    <div className="absolute top-3 left-3">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${categoryColors[item.category]}`}
                      >
                        {item.tag}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20 backdrop-blur-sm">
                        <i className="ri-arrow-right-line text-[10px] text-white" />
                      </span>
                    </div>
                  </Link>

                  <div className="p-5 md:p-6">
                    <div className="flex items-center gap-3 text-xs text-tie-text-muted mb-3">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-calendar-line" />
                        {item.date}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-time-line" />
                        {item.readTime}
                      </span>
                    </div>
                    <Link
                      to={`/press-release/${item.id}`}
                      className="block"
                    >
                      <h3 className="text-base md:text-lg font-semibold text-tie-text-primary mb-2 leading-snug group-hover:text-tie-teal transition-colors">
                        {item.title}
                      </h3>
                    </Link>
                    <p className="text-sm text-tie-text-secondary leading-relaxed line-clamp-3">
                      {item.excerpt}
                    </p>
                    <div className="mt-4 pt-4 border-t border-tie-grey-dark">
                      <Link
                        to={`/press-release/${item.id}`}
                        className="inline-flex items-center text-sm font-medium text-tie-teal hover:underline"
                      >
                        Read article
                        <i className="ri-arrow-right-line ml-1.5 text-xs opacity-60" />
                      </Link>
                    </div>
                  </div>
                </article>
              </ScrollReveal>
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <ScrollReveal>
              <div className="mt-10 md:mt-12 text-center">
                <button
                  onClick={() => setVisibleCount((c) => c + 9)}
                  className="inline-flex items-center justify-center px-8 py-3.5 rounded-full bg-tie-navy text-white font-medium text-sm hover:bg-tie-navy/90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Load More Releases
                  <i className="ri-arrow-down-line ml-2" />
                </button>
              </div>
            </ScrollReveal>
          )}

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-tie-grey flex items-center justify-center">
                <i className="ri-newspaper-line text-2xl text-tie-text-muted" />
              </div>
              <p className="text-base text-tie-text-secondary">
                No releases in this category yet.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}